import { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const PLAN_HIERARCHY = {
  gratis: 0,
  basico: 1,
  pro: 2,
  admin: 3,
};

const PLAN_LIMITS = {
  gratis: {
    label: 'Gratuito',
    maxOrcamentos: 1,
    maxComodos: 2,
    exportPdf: false,
    exportExcel: false,
    historicoCompleto: false,
    apiAccess: false,
  },
  basico: {
    label: 'Básico',
    maxOrcamentos: 10,
    maxComodos: Infinity,
    exportPdf: true,
    exportExcel: true,
    historicoCompleto: false,
    apiAccess: false,
  },
  pro: {
    label: 'Pro',
    maxOrcamentos: Infinity,
    maxComodos: Infinity,
    exportPdf: true,
    exportExcel: true,
    historicoCompleto: true,
    apiAccess: true,
  },
  admin: {
    label: 'Admin',
    maxOrcamentos: Infinity,
    maxComodos: Infinity,
    exportPdf: true,
    exportExcel: true,
    historicoCompleto: true,
    apiAccess: true,
  },
};

export default function usePlan() {
  const { user } = useAuth();
  const [dbPlan, setDbPlan] = useState(null);

  // Fetch plan directly from profiles table (source of truth)
  useEffect(() => {
    if (!user?.id) {
      setDbPlan(null);
      return;
    }

    let cancelled = false;

    supabase
      .from('profiles')
      .select('plano')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (!cancelled) {
          setDbPlan(data?.plano || null);
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [user?.id]);

  const plan = useMemo(() => {
    const planId = dbPlan || 'gratis';
    const limits = PLAN_LIMITS[planId] || PLAN_LIMITS.gratis;
    const level = PLAN_HIERARCHY[planId] ?? 0;

    return {
      id: planId,
      ...limits,
      level,
      isFreePlan: level === 0,
      canExportPdf: limits.exportPdf,
      canExportExcel: limits.exportExcel,
      hasFullAccess: level >= 2,
    };
  }, [dbPlan]);

  const hasFeature = (feature) => {
    return !!plan[feature];
  };

  const meetsMinPlan = (minPlan) => {
    const minLevel = PLAN_HIERARCHY[minPlan] ?? 0;
    return plan.level >= minLevel;
  };

  return { plan, hasFeature, meetsMinPlan };
}

export { PLAN_HIERARCHY, PLAN_LIMITS };
